import React from 'react'

import classNames from 'clsx'
import PropTypes from 'prop-types'
import { prop } from 'ramda'

import { Avatar, Box, Typography } from '@mui/material'

import {
  atLeastOneRequired,
  datePropType,
  defaultInvalidMsg,
  regexPropType,
} from '~/src/Lib/PropTypes'
import { DATE_FORMATS, EMPTY_OBJECT } from '~/src/Lib/Utils'
import { priorityOptions } from '~/src/Task/constants'
import FormattedDate from '~/src/UI/Shared/FormattedDate'

import CommentAmount from './CommentAmount'

export const renderProgressStatus = progress => (
  <Box
    component={progress}
    color="error.main"
    ml="5px"
    fontSize="small"
    className="status"
  />
)

export const renderCommentStatus = comments => (
  <CommentAmount count={comments.length} className="status" />
)

export const renderPriorityStatus = priority => {
  const options = priorityOptions.find(obj => obj.value === priority)
  const { icon: Icon } = options ?? EMPTY_OBJECT
  return Icon ? (
    <Box
      color={options.iconColor}
      display="contents"
      ml="5px"
      fontSize="1.25rem"
      className="status"
    >
      <Icon fontSize="inherit" color="inherit" />
    </Box>
  ) : null
}

const defaultDateFormat = 'SHORT_DATETIME'
export const defaultFields = ['body', 'date', 'location']
const defaultMultiDateFormat = 'TINY_DATE'
export const DEFAULT_AVATAR_SIZE = 40

export const dateFormatType = PropTypes.oneOfType([
  PropTypes.objectOf(PropTypes.string),
  PropTypes.oneOf(Object.keys(DATE_FORMATS))
])

export const propTypes = {
  ...atLeastOneRequired({
    title: PropTypes.node,
    body: PropTypes.node
  }),
  annotationKey: PropTypes.string.isRequired,
  avatarSize: PropTypes.number,
  bare: PropTypes.bool,
  color: PropTypes.oneOf([
    'error',
    'inherit',
    'initial',
    'primary',
    'secondary',
    'textPrimary',
    'textSecondary',
  ]),
  date: (props, name, component) => {
    const { [name]: date } = props
    if (React.isValidElement(date)) return undefined

    if (Array.isArray(date)) {
      return date.some((_, index) => datePropType(date, index, name))
        ? new Error(`${defaultInvalidMsg(date, name, component)}. Expected an array of valid date strings or objects.`)
        : undefined
    }

    return datePropType(props, name, component)
  },
  dateFormat: dateFormatType,
  multiDateFormat: dateFormatType,
  dateLabel: PropTypes.string,
  extra: PropTypes.node,
  // This prop is used in the isFieldAllowed check function
  // eslint-disable-next-line react/no-unused-prop-types
  fields: PropTypes.oneOfType([
    regexPropType(/^all$/),
    PropTypes.arrayOf(regexPropType(/body|date|location|extra|user/))
  ]),
  inline: PropTypes.bool,
  noButton: PropTypes.bool,
  noDialogs: PropTypes.bool,
  tooltip: PropTypes.bool,
  wrap: PropTypes.bool,
  onClick: PropTypes.func,
  room: PropTypes.shape({ name: PropTypes.string }),
  user: PropTypes.shape({ fullName: PropTypes.string, avatarUrl: PropTypes.shape({ url: PropTypes.string }) }),
  Wrapper: PropTypes.elementType,
  zone: PropTypes.shape({ name: PropTypes.string }),
}

export const defaultProps = {
  avatarSize: DEFAULT_AVATAR_SIZE,
  bare: false,
  body: null,
  color: 'inherit',
  date: null,
  dateFormat: defaultDateFormat,
  multiDateFormat: defaultMultiDateFormat,
  dateLabel: null,
  extra: null,
  fields: defaultFields,
  inline: false,
  noButton: false,
  noDialogs: false,
  tooltip: false,
  wrap: false,
  onClick: undefined,
  user: null,
  room: null,
  Wrapper: null,
  zone: null,
}

export const fieldIsAllowed = (field, { fields = defaultProps.fields }) => fields === 'all' || fields.includes(field)

const getAvatarStyle = (avatar, initials, { avatarSize }) => ({
  width: avatarSize,
  height: avatarSize,
  fontSize: `${((avatarSize / DEFAULT_AVATAR_SIZE) * 1.25).toFixed(3)}rem`,
  padding: avatar ? 0 : `10px ${20 - (initials.length * 5)}px`
})

const getTitle = (classes, props) => {
  const { annotationKey, inline, title, wrap } = props
  return React.isValidElement(title)
    ? React.cloneElement(title, {
      className: classNames(classes.title, title.props.className),
      key: `${annotationKey}-title`
    })
    : (
      <Typography
        key={`${annotationKey}-title`}
        noWrap={!wrap}
        className={classes.title}
        variant="body1"
        display={inline ? 'inline' : 'initial'}
        color="textPrimary"
      >
        {title}
      </Typography>
    )
}

const getBody = (classes, props) => {
  const { annotationKey, body, inline, wrap } = props
  const key = `${annotationKey}-body`
  if (!body || !fieldIsAllowed('body', props)) {
    return <span key={key} className={classes.body} />
  }

  return React.isValidElement(body)
    ? React.cloneElement(body, {
      className: classNames(classes.body, body.props.className),
      key
    })
    : (
      <Typography
        key={key}
        noWrap={!wrap}
        variant="body2"
        component="div"
        display={inline ? 'inline' : 'initial'}
        className={classes.body}
      >
        {body}
      </Typography>
    )
}

const getDate = (classes, props) => {
  const { annotationKey, date, dateFormat, dateLabel, multiDateFormat } = props
  const key = `${annotationKey}-date`
  if (!date || !fieldIsAllowed('date', props)) {
    return <span key={key} className={classes.date} />
  }

  if (React.isValidElement(date)) {
    return React.cloneElement(date, {
      className: classNames(classes.date, date.props.className),
      key
    })
  }

  return (
    <Typography
      key={key}
      data-testid="annotation-formatters-date"
      variant="body2"
      className={classes.date}
    >
      {!Array.isArray(date) ? (
        <FormattedDate
          native
          label={dateLabel}
          date={date}
          format={dateFormat}
          component="span"
        />
      ) : (
        <>
          <FormattedDate
            native
            label={dateLabel}
            date={date[0]}
            format={multiDateFormat}
            component="span"
          />
          {date[1] ? '\u2013' : null}
          {date[1] ? (
            <FormattedDate
              native
              display="inline"
              label={dateLabel}
              date={date[1]}
              format={multiDateFormat}
              component="span"
            />
          ) : null}
        </>
      )}
    </Typography>
  )
}

const getLocation = (classes, props) => {
  const { annotationKey, room, zone } = props
  const location = room || zone
  const key = `${annotationKey}-location`
  if (!location || !fieldIsAllowed('location', props)) {
    return <span key={key} className={classes.location} />
  }

  return React.isValidElement(location)
    ? React.cloneElement(location, {
      className: classNames(classes.location, location.props.className),
      key
    })
    : (
      <Typography
        key={`${annotationKey}-location`}
        display="inline"
        variant="body2"
        component="span"
        className={classes.location}
      >
        {[room, zone].filter(Boolean).map(prop('name')).join(', ')}
      </Typography>
    )
}

const getUser = (classes, props) => {
  const { filestack } = props
  const { annotationKey, user } = props
  const initials = user ? [user?.firstName, user?.lastName].filter(Boolean).map(name => name[0]) : ''
  const avatar = user?.avatarUrl && typeof filestack === 'function' ? filestack(
    user.avatarUrl,
    [`resize=width:${DEFAULT_AVATAR_SIZE},height:${DEFAULT_AVATAR_SIZE},fit:crop,align:center`]
  ) : null
  const key = `${annotationKey}-user`
  if (!user || !fieldIsAllowed('user', props) || !(user?.avatarUrl ?? initials)) {
    return <span key={key} className={classes.user} />
  }

  return React.isValidElement(user)
    ? React.cloneElement(user, {
      className: classNames(classes.user, user.props.className),
      key
    })
    : (
      <span key={key} className={classes.user} title={user.fullName}>
        <Avatar
          alt={user?.fullName || 'User Name'}
          src={avatar}
          className={classes.avatar}
          style={getAvatarStyle(avatar, initials, props)}
        >
          {avatar ? null : initials}
        </Avatar>
      </span>
    )
}

const getters = [
  getTitle,
  getBody,
  getDate,
  getLocation,
  getUser
]

export const getContent = (classes, props) => getters.map(getter => getter(classes, props))

export const getFirstImage = attachments => {
  if (!Array.isArray(attachments)) return undefined
  return attachments.find(attachment => attachment?.mimetype?.startsWith('image'))
}

export const getFirstAttachment = attachments => {
  if (!Array.isArray(attachments)) return undefined
  return getFirstImage(attachments) || attachments[0]
}
